<script setup>
import { computed, ref, watch } from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { Button } from '@dashhudson/dashing-ui/button';
import { FilterPanel } from '@dashhudson/dashing-ui/filter-panel';
import { FormItem } from '@dashhudson/dashing-ui/form';
import { InputText } from '@dashhudson/dashing-ui/input-text';
import { Select } from '@dashhudson/dashing-ui/select';
import { debounceSearch } from '@/config';
import { refDebounced } from '@vueuse/core';

const props = defineProps({
  contains: {
    type: Array,
    default: () => [],
  },
  notContains: {
    type: Array,
    default: () => [],
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  title: {
    type: String,
    default: 'Caption',
  },
  showRemoveButton: {
    type: Boolean,
    default: false,
  },
});

const operatorOptions = [
  { label: 'Contains', value: 'containKeywords' },
  { label: 'Does Not Contain', value: 'notContainKeywords' },
];

const emit = defineEmits(['update:contains', 'update:notContains']);

const postCaptionGroups = ref([]);

function defaultPostCaptionGroup() {
  return {
    operator: operatorOptions[0].value,
    keyword: '',
  };
}

const addPostCaptionGroup = () => {
  postCaptionGroups.value.push(defaultPostCaptionGroup());
};

const validPostCaptionGroups = computed(() => {
  const groups = cloneDeep(postCaptionGroups.value);
  return groups.filter((group) => group?.keyword?.length > 0);
});

function syncGroupsFromProps() {
  const newGroups = [];
  const oldGroups = postCaptionGroups.value;
  const containsSet = new Set(props.contains);
  const notContainsSet = new Set(props.notContains);

  oldGroups.forEach((group) => {
    if (group.operator === 'containKeywords' && containsSet.has(group.keyword)) {
      newGroups.push(cloneDeep(group));
      containsSet.delete(group.keyword);
    } else if (group.operator === 'notContainKeywords' && notContainsSet.has(group.keyword)) {
      newGroups.push(cloneDeep(group));
      notContainsSet.delete(group.keyword);
    }
  });

  containsSet.forEach((keyword) => {
    newGroups.push({ operator: 'containKeywords', keyword });
  });
  notContainsSet.forEach((keyword) => {
    newGroups.push({ operator: 'notContainKeywords', keyword });
  });

  if (!newGroups.length) {
    newGroups.push(defaultPostCaptionGroup());
  }

  postCaptionGroups.value = newGroups;
}

watch(
  () => [props.contains, props.notContains],
  (to, from) => {
    if (isEqual(to, from)) return;
    syncGroupsFromProps();
  },
  { immediate: true, deep: true },
);

const debouncedValidGroups = refDebounced(validPostCaptionGroups, debounceSearch);

watch(
  debouncedValidGroups,
  (groups) => {
    const contains = groups.filter((g) => g.operator === 'containKeywords').map((g) => g.keyword);
    const notContains = groups
      .filter((g) => g.operator === 'notContainKeywords')
      .map((g) => g.keyword);
    if (!isEqual(contains, props.contains)) {
      emit('update:contains', contains);
    }
    if (!isEqual(notContains, props.notContains)) {
      emit('update:notContains', notContains);
    }
  },
  { deep: true },
);
</script>

<template>
  <FilterPanel :title="props.title" no-remember>
    <div class="!flex !flex-col !gap-4">
      <div
        v-for="(group, index) in postCaptionGroups"
        :key="index"
        class="dash-flex dash-flex-col dash-gap-4"
      >
        <div class="!mb-1 !flex !flex-col !gap-3">
          <FormItem>
            <Select v-model="group.operator" :options="operatorOptions" />
          </FormItem>
          <FormItem v-slot="{ inputProps, inputEvents }" name="keyword" required>
            <div class="!flex !w-full !items-center !gap-2">
              <InputText
                v-bind="inputProps"
                v-model="group.keyword"
                placeholder="Enter Keywords"
                class="!w-full"
                clearable
                v-on="inputEvents"
              />
              <Button
                v-if="props.multiple && props.showRemoveButton"
                icon="delete"
                severity="danger"
                aria-label="Remove Keyword"
                :disabled="postCaptionGroups.length === 1"
                size="xs"
                class="!shrink-0"
                @click="postCaptionGroups.splice(index, 1)"
              />
            </div>
          </FormItem>
        </div>
      </div>
      <Button
        v-if="props.multiple"
        aria-label="Add Keyword"
        icon="plus"
        label="Add Keyword"
        severity="utility"
        @click="addPostCaptionGroup"
      />
    </div>
  </FilterPanel>
</template>
